import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import {
  resolveOrgDisclaimerDisplay,
  sanitiseOrgDisclaimerHtml,
  type OrgDisclaimerContentLocale,
} from "@/helpers";
import { authApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { ApiError } from "@/types";

function OrgDisclaimerGateScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, refreshCurrentUserAccess } = useAuth();
  const { showToast } = useToast();
  const [isAccepting, setIsAccepting] = useState(false);
  const [localeOverride, setLocaleOverride] =
    useState<OrgDisclaimerContentLocale | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);

  const orgDisclaimer = user?.org_disclaimer;
  const currentVersion = orgDisclaimer?.current_version;
  const logoUrl = user?.user.organization?.logo_url;

  const display = useMemo(
    () =>
      resolveOrgDisclaimerDisplay(
        orgDisclaimer,
        i18n.language,
        localeOverride ?? undefined
      ),
    [orgDisclaimer, i18n.language, localeOverride]
  );

  const sanitisedHtml = useMemo(() => {
    if (!display.isHtml) {
      return null;
    }
    return sanitiseOrgDisclaimerHtml(display.displayBody);
  }, [display.displayBody, display.isHtml]);

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleAccept = async () => {
    if (!currentVersion || isAccepting) {
      return;
    }

    setIsAccepting(true);
    try {
      await authApi.acceptOrgDisclaimer(currentVersion);
      await refreshCurrentUserAccess();
    } catch (error) {
      const apiError = error as ApiError;
      if (
        apiError.code === "CONFLICT" &&
        apiError.backendData?.org_disclaimer
      ) {
        await refreshCurrentUserAccess();
        return;
      }
      showToast({
        title: t("features.auth.orgDisclaimer.acceptErrorTitle"),
        description: t("features.auth.orgDisclaimer.acceptErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <Dialog open modal>
      <DialogContent
        data-testid="org-disclaimer-gate"
        showCloseButton={false}
        overlayClassName="bg-black/50 backdrop-blur-[1px]"
        className={cn(
          "fixed left-[50%] top-[50%] z-50 flex max-h-[90vh] w-[min(100%,720px)] translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden rounded-lg border bg-background p-0 shadow-lg sm:max-w-[720px]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        )}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="space-y-0 px-6 pb-px pt-6 text-left">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex h-9 items-center">
              {logoUrl && !logoFailed ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="max-h-9 w-auto max-w-[160px] object-contain object-left"
                  onError={() => setLogoFailed(true)}
                />
              ) : null}
            </div>
            {display.showLocaleSwitch ? (
              <div
                className="inline-flex h-[26px] shrink-0 overflow-hidden rounded-md border border-input"
                role="group"
                aria-label={t("features.auth.orgDisclaimer.localeSwitch")}
              >
                <button
                  type="button"
                  className={cn(
                    "h-full px-3 text-[10px] font-bold leading-none",
                    display.selectedKey === "french"
                      ? "bg-foreground text-background"
                      : "bg-background text-muted-foreground"
                  )}
                  aria-pressed={display.selectedKey === "french"}
                  onClick={() => setLocaleOverride("french")}
                >
                  {t("features.auth.orgDisclaimer.localeFr")}
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-full px-3 text-[10px] font-bold leading-none",
                    display.selectedKey === "english"
                      ? "bg-foreground text-background"
                      : "bg-background text-muted-foreground"
                  )}
                  aria-pressed={display.selectedKey === "english"}
                  onClick={() => setLocaleOverride("english")}
                >
                  {t("features.auth.orgDisclaimer.localeEn")}
                </button>
              </div>
            ) : null}
          </div>
          <DialogTitle className="text-left text-base font-semibold leading-snug tracking-tight">
            {t("features.auth.orgDisclaimer.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("features.auth.orgDisclaimer.title")}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {display.isHtml && sanitisedHtml !== null ? (
            <div
              className="org-disclaimer-body max-w-none text-left text-xs leading-5 text-foreground [&_li]:my-1 [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: sanitisedHtml }}
            />
          ) : (
            <p className="whitespace-pre-wrap text-left text-xs leading-5 text-foreground">
              {display.displayBody}
            </p>
          )}
        </div>

        <DialogFooter className="flex flex-row items-center justify-between gap-3 space-x-0 border-t border-border px-6 py-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-auto shrink-0 px-5 text-xs"
            onClick={handleSignOut}
            disabled={isAccepting}
          >
            {t("features.auth.accessDenied.signOut")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="w-auto shrink-0 px-5 text-xs"
            onClick={handleAccept}
            disabled={!currentVersion || isAccepting}
          >
            {t("features.auth.orgDisclaimer.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OrgDisclaimerGateScreen;
